/**
 * Findings Lifecycle Guide — static lesson at /my/learn/findings-lifecycle.
 *
 * Two stories, one page:
 *   1. The lifecycle of a finding from "agent saw it" to "fixed".
 *   2. How Trinetra dedupes findings + auto-fix PRs across runs (SARIF
 *      partial fingerprints) so re-running an agent doesn't create
 *      noise.
 *
 * Linked from /my/learn.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, GitPullRequest, Repeat, Workflow } from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  FindingLifecycleDiagram,
  DedupDecisionDiagram,
  PrDedupDiagram,
} from '@/components/learn/findings-lifecycle-diagrams';
import { cn } from '@/lib/utils';

const STATUSES: Array<{ status: string; tone: string; meaning: string }> = [
  { status: 'open',           tone: 'bg-red-500/15 text-red-600 dark:text-red-400',         meaning: 'Agent reported it. Nothing has happened yet — it shows on the dashboard, counts against the score.' },
  { status: 'fix-merged',     tone: 'bg-india-green/15 text-india-green',                   meaning: 'A Trinetra-opened PR for this finding was merged on GitHub. Awaiting the next scan to confirm the fix actually landed in code.' },
  { status: 'fixed',          tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', meaning: 'The next scan looked at the same area and the bug is gone. Stops counting against the score.' },
  { status: 'dismissed',      tone: 'bg-muted text-muted-foreground',                       meaning: 'A user marked it as not-a-bug. Stays closed; agents won\'t reopen it on regression — they\'ll just bump lastSeenAt.' },
  { status: 'accepted-risk',  tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',   meaning: 'A user acknowledged the risk and chose to keep it. Same treatment as dismissed: stays closed across re-runs.' },
];

// All three diagrams are rendered as hand-authored SVG components (see
// components/learn/findings-lifecycle-diagrams.tsx). We tried Mermaid
// earlier; the web app's strict CSP (`style-src 'self'` and
// `style-src-attr 'none'`) made its inline-styled SVGs render as black
// triangle wedges instead of clean arrows. Pure-SVG components ship
// presentation as attributes (`stroke=…`), which CSP doesn't touch.

const SARIF_FPS: Array<{ key: string; survives: string; breaks: string }> = [
  {
    key: 'primaryLocationLineHash/v1',
    survives: 'Lines moving up/down. Imports added above. Renames of unrelated functions in the same file.',
    breaks: 'Reformatting the buggy line (e.g. running prettier on it). Comment changes on that exact line.',
  },
  {
    key: 'contextRegionHash/v1',
    survives: 'Whitespace tweaks. Comment-only edits in the surrounding region. Single-line drift.',
    breaks: 'Refactoring the surrounding function. Changing variable names referenced in the snippet.',
  },
  {
    key: 'ruleId/v1',
    survives: 'Almost any code edit — only breaks if you re-categorise the rule.',
    breaks: 'Renaming the category itself (e.g. "injection" → "sql-injection-strict"). Last-resort fallback only.',
  },
];

export function FindingsLifecycleGuidePage() {
  return (
    <div className="space-y-8 max-w-4xl">
      {/* Page header */}
      <div className="space-y-3">
        <Link to="/my/learn" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Learn
        </Link>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
            <Workflow className="h-5 w-5 text-primary-strong" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">The lifecycle of a finding</h1>
            <p className="text-sm text-muted-foreground">
              How Trinetra tracks a finding from <em>agent reported</em> through <em>fix merged</em> to <em>fixed</em>,
              and how we keep re-running the same scan from drowning you in duplicates.
            </p>
          </div>
        </div>
      </div>

      {/* Section 1 — Statuses */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">1. The five statuses</h2>
        <p className="text-sm text-foreground/90">
          Every finding is in exactly one of five states. The transitions are mostly automatic; the only
          user-driven moves are <code className="text-xs">dismissed</code> and{' '}
          <code className="text-xs">accepted-risk</code>.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">What it means</th>
              </tr>
            </thead>
            <tbody>
              {STATUSES.map((row) => (
                <tr key={row.status} className="border-b border-border/60 align-top">
                  <td className="py-2 px-3 whitespace-nowrap">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider', row.tone)}>
                      {row.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-foreground/90">{row.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Section 2 — State machine */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">2. The state machine</h2>
        <p className="text-sm text-foreground/90">
          The transitions are deterministic — every arrow is a specific runtime event (an agent observation, a
          GitHub webhook, a user click). No hidden behaviour.
        </p>
        <FindingLifecycleDiagram />
        <Card className="p-4 bg-muted/30 text-sm text-foreground/90 space-y-2">
          <p className="font-semibold">Three transitions worth understanding well:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong>open → fix-merged</strong> — driven by GitHub's <code className="text-xs">pull_request closed</code> webhook with <code className="text-xs">merged=true</code>. We look up <code className="text-xs">finding_fixes</code> rows linked to that PR; every linked finding flips to <code className="text-xs">fix-merged</code>. The merge SHA + PR URL are stamped on the row.</li>
            <li><strong>fix-merged → fixed</strong> — happens at the end of the next agent run that demonstrably <em>scanned the same category</em>. The harness checks: did this run emit any finding in the same category, and is the fix-merged finding's fingerprint absent from this run's output? If both, the bug's gone — promote to <code className="text-xs">fixed</code>. Conservative on purpose; a false positive here would say "fixed" while the bug is still live.</li>
            <li><strong>fix-merged or fixed → open (regression)</strong> — same finding fingerprint shows up on a subsequent run. Description is prefixed with <em>"Regression detected on run X"</em> so reviewers see why it's back.</li>
          </ul>
        </Card>
      </section>

      {/* Section 3 — Dedup */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">3. Dedup: SARIF partial fingerprints</h2>
        <p className="text-sm text-foreground/90">
          Re-running the same agent over the same code shouldn't add new finding rows for the same bug. Trinetra
          uses{' '}
          <a
            href="https://docs.oasis-open.org/sarif/sarif/v2.1.0/os/sarif-v2.1.0-os.html#_Toc34317718"
            target="_blank"
            rel="noreferrer"
            className="text-primary-strong hover:underline"
          >
            SARIF partial fingerprints
          </a>{' '}
          — a small map of named hashes attached to every finding. Each hash survives a different kind of code
          edit, so we can pick the strongest one that still matches across runs.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Fingerprint</th>
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Survives</th>
                <th className="text-left py-2 px-3 font-semibold text-muted-foreground text-xs uppercase tracking-wider">Breaks on</th>
              </tr>
            </thead>
            <tbody>
              {SARIF_FPS.map((row) => (
                <tr key={row.key} className="border-b border-border/60 align-top">
                  <td className="py-2 px-3 font-mono text-xs whitespace-nowrap">{row.key}</td>
                  <td className="py-2 px-3 text-foreground/90">{row.survives}</td>
                  <td className="py-2 px-3 text-foreground/90">{row.breaks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-sm text-foreground/90">
          We denormalise the strongest available fingerprint (<code className="text-xs">contextRegionHash/v1</code>,
          falling back to <code className="text-xs">primaryLocationLineHash/v1</code>) into a{' '}
          <code className="text-xs">dedup_key</code> column with a partial unique index on{' '}
          <code className="text-xs">(project_id, dedup_key)</code>. That gives the database one source of truth
          for "is this the same finding?"
        </p>

        <DedupDecisionDiagram />
      </section>

      {/* Section 4 — PR dedup */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">4. Pull-request dedup</h2>
        <p className="text-sm text-foreground/90 flex items-start gap-2">
          <GitPullRequest className="h-4 w-4 text-india-green mt-0.5 shrink-0" />
          <span>
            Auto-fix PRs use the same fingerprint as the dedup key, so re-finding the same bug on a later run
            updates the existing PR instead of opening a new one. The branch name is{' '}
            <code className="text-xs">trinetra/&lt;rule-slug&gt;-&lt;contextRegionHash-prefix&gt;</code> — short,
            stable, and readable on github.com.
          </span>
        </p>

        <PrDedupDiagram />

        <Card className="p-4 bg-muted/30 text-sm text-foreground/90">
          <div className="flex items-start gap-2">
            <Repeat className="h-4 w-4 text-primary-strong mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">In practice:</p>
              <p className="text-foreground/80 mt-1">
                Run <code className="text-xs">code-vuln</code> on a project. It finds an SQL injection in
                <code className="text-xs">/v1/orders/search</code> and opens PR <code className="text-xs">#42</code>{' '}
                on a branch like <code className="text-xs">trinetra/sql-injection-a3f1c0e9b240</code>.
                Re-run <code className="text-xs">code-vuln</code> tomorrow before merging — the same fingerprint
                resolves to the same branch, the tool re-PUTs the same files, no new PR opens. PR <code className="text-xs">#42</code>{' '}
                is the canonical fix; merging it flips the finding to <code className="text-xs">fix-merged</code>.
              </p>
            </div>
          </div>
        </Card>
      </section>

      {/* Section 5 — Why a two-stage merge → fixed */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">5. Why merging doesn't immediately mark a finding as fixed</h2>
        <p className="text-sm text-foreground/90">
          Merging a PR is necessary but not sufficient. Reviewers can edit the patch on the way through review,
          a second occurrence of the same pattern may live elsewhere, or the merge may revert during a release.
          So we trust the merge as a strong signal — flip to <code className="text-xs">fix-merged</code> — but
          defer the truth claim to the agent re-running on the merged commit.
        </p>
        <p className="text-sm text-foreground/90">
          The <code className="text-xs">push</code> webhook on the project's tracked branch automatically queues a
          <code className="text-xs"> smart-scan</code>, so the second stage happens for free without any user
          action. If the bug is gone, status becomes <code className="text-xs">fixed</code>. If it isn't, the
          finding reopens with a <em>"Regression detected"</em> note — and the score goes back up to where it
          was, signalling that the fix didn't take.
        </p>
      </section>
    </div>
  );
}
